import * as React from 'react';

import { useAtomValue } from 'jotai';
import { setRoute, loadTranslation } from '@src/services/helpers/appHelpers';
import { style, stylesheet } from 'typestyle';
import { Services, ServiceName } from '@src/types/Services';
import { Login as LoginService } from '@src/types/Login';
import { Pathname } from '@src/types/Browsing';
import { Loader } from '@src/components/Loader';
import { Row } from '@src/components/Utils';
import { FormError } from '@src/components/Form/FormError';
import { Link } from '@src/components/Link';
import MailLink from '@src/components/MailLink';
import { ADALONG_COLORS, ThemeStyle } from '@src/styles';
import { loginSubmittingAtom, loginErrorAtom, loginLoggedAtom } from '@src/state/loginAtoms';
import { LoginSigninLabel } from './SigninLabel';
import { LoginFrameContainer } from './container/FrameContainer';
import { LoginContainer } from './container/LoginContainer';
import UserLogin from './UserLogin';
import logo from '../../assets/img/site/logo_alone_white.png';

const ADALONG_MAIL = 'hello@adalong.com';

let loginService: LoginService;

export default function Login(): JSX.Element {
  const submitting = useAtomValue(loginSubmittingAtom);
  const error = useAtomValue(loginErrorAtom);
  const logged = useAtomValue(loginLoggedAtom);

  let errorElement: string | JSX.Element | undefined;
  if (error !== null) {
    try {
      isValidError(error);
      errorElement = lang.errors[error] || lang.errors.unknown;
    } catch (e) {
      errorElement = lang.errors.unknown;
    }
  }

  return (
    <LoginContainer>
      <LoginFrameContainer
        header={(
          <>
            <div className={sheet.loginLogo}>
              <img src={logo} />
            </div>
          </>
        )}
      >
        <LoginSigninLabel title={window.T('login.signin')} />
        {!submitting && !logged
          ? (
            <UserLogin
              login={loginService?.emailLogin.bind(loginService)}
              emailLogin={loginService?.emailLogin.bind(loginService)}
              uiConfig={loginService?.getFirebaseUiConfig()}
            />
          ) : null}
          {
            submitting ?
            <div className={loaderClass}>
              <Loader size={30} />
            </div>
          : null
          }
        <Row className={sheet.forgotten}>
          <Link onClick={() => setRoute(Pathname.changePassword)}>{window.T('login.changeyourpassword')}</Link>
        </Row>
        <Row className={sheet.onboard}>
          <Link onClick={() => setRoute(Pathname.onboard)}>{lang.onboard}</Link>
        </Row>
        <Row className={sheet.links}>
          <span>
            By signing in you will accept our &nbsp;
            <br />
            <Link className={sheet.link} href="https://www.adalong.com/terms-of-use/" target="_blank" rel="noreferrer">
              Terms of Service
            </Link>
            &nbsp;and&nbsp;
            <Link className={sheet.link} href="https://www.adalong.com/privacy/" target="_blank" rel="noreferrer">
              Privacy Policy
            </Link>
          </span>
        </Row>
        <p className={sheet.signUp}>
          If you don't have an account, please contact us at
          {' '}
          <MailLink mail={ADALONG_MAIL}>{ADALONG_MAIL}</MailLink>
        </p>
        <FormError
          error={errorElement}
          level="error"
        />
      </LoginFrameContainer>
    </LoginContainer>
  );
}

const loaderClass = style({ margin: 15 });

const sheet = stylesheet({
  loginLogo: {
    flexBasis: 0,
    margin: '0 auto',
    $nest: {
      img: {
        height: 80,
        margin: '5 auto 20px',
      },
    },
  },
  forgotten: {
    cursor: 'pointer',
    fontSize: 10,
    color: '#007bff',
    marginBottom: 10,
    marginTop: 10,
    justifyContent: 'center',
  },
    onboard: {
    cursor: 'pointer',
    fontSize: 10,
    color: '#007bff',
    marginBottom: 10,
    justifyContent: 'center',
  },

  links: {
    justifyContent: 'center',
    fontSize: 12,
    color: '#CCC',
  },
  link: {
    fontSize: 15,
  },
  signUp: {
    fontSize: ThemeStyle.textSizes.small,
    color: ADALONG_COLORS.LIGHT_GRAY,
  },
});

function isValidError(error: string): asserts error is ValidError {
  if (!(error in lang.errors)) {
    throw Error(`Invalid error ${error}`);
  }
}

const translation = {
  en: {
    onboard: 'First time? Click here to set up your account',
    errors: {
      authusernotfound: 'Invalid user account',
      unsufficientrights: 'Invalid user account',
      nonexistinguser: <div>
        Your account was not created yet, please contact us at
        {' '}
        <MailLink mail={ADALONG_MAIL}>{ADALONG_MAIL}</MailLink>
        {' '}
        to register
      </div>,
      unregistered: <div>
        Your account was not created yet, please contact us at
        {' '}
        <MailLink mail={ADALONG_MAIL}>{ADALONG_MAIL}</MailLink>
        {' '}
        to register
      </div>,
      invalid: 'Invalid username or password',
      unknownemail: 'Unknown email',
      unknown: 'An error occured, please try again later',
      unverifiedEmail: 'Your email address was not verified, please check your emails',
      cantSendEmail: 'Could not send a validation email, please try again later or contact us',
      resetexpired: 'This link no longer seems valid',
      sessionlocked: 'Your session was locked due to too many unsuccessful attempts, please wait for another 30 minutes',
      authwrongpassword: 'The password is invalid or the user does not have a password',
      authinvalidemail: 'The email address is badly formatted.',
    },
  },
};

const lang = loadTranslation(translation);
type ValidError = keyof typeof lang.errors;

export function LoginDep(services: Services) {
  loginService = services.get(ServiceName.Login);
  return Login;
}
